import { useMutation } from '@tanstack/react-query';
import { apiPath, apiUpload, apiDelete } from '@/lib/api/client';
import { CubixApiError } from '@/lib/api/types';
import type { ImportResult, ProductType } from './types';
import { PRODUCT_TYPES } from './types';

// ── POST /api/v1/alm/data/import/{type} ──────────────────────────────────────

interface ImportPositionVars {
  type: ProductType;
  file: File;
}

/**
 * Uploads a product-position CSV to the backend.
 *
 * On success the backend has:
 *   1. Cleared that product's existing bronze.deals rows for today.
 *   2. Inserted the new rows and rebuilt gold.gap_cf_schedule + lcr_classification.
 *   3. Run all 4 engines (Gap, NIM, LCR, EVE) with force=true.
 *
 * The response carries per-engine result IDs and any engine-level error messages.
 */
export function useImportPositions() {
  return useMutation<ImportResult, CubixApiError, ImportPositionVars>({
    mutationFn: ({ type, file }) =>
      apiUpload<ImportResult>(
        apiPath('alm', 'data', 'import', type),
        file,
        {},
        'file',
      ),
  });
}

// ── Client-side template download ─────────────────────────────────────────────

/**
 * Generates the CSV template content client-side and triggers a browser download.
 * No backend call needed — avoids JWT auth issues with plain <a href> links.
 */
export function downloadTemplate(type: ProductType): void {
  const meta = PRODUCT_TYPES[type];
  const blob = new Blob([meta.templateCsv], { type: 'text/csv;charset=utf-8;' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = meta.templateFile;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ── DELETE /api/v1/alm/data/positions ────────────────────────────────────────

interface ClearResult {
  asOfDate: string;
  status:   string;
}

/** Clears all manually imported positions for today and wipes the gold snapshot. */
export function useClearPositions() {
  return useMutation<ClearResult, CubixApiError, void>({
    mutationFn: () =>
      apiDelete<ClearResult>(apiPath('alm', 'data', 'positions')),
  });
}
